import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { ROLES, EMPLOYMENT_TYPES, SHIFTS } from "@/lib/content";
import { Eyebrow, Reveal } from "./Reveal";
import { photoSet } from "@/lib/img";

const Chips = ({ title, items, id }) => (
  <div data-testid={id}>
    <p className="text-xs uppercase tracking-[0.22em] text-[#E2C372] mb-3">{title}</p>
    <div className="flex flex-wrap gap-2">
      {items.map((t) => <span key={t} className="px-4 py-2 rounded-full border border-[#FBF8F1]/25 text-sm text-[#FBF8F1]">{t}</span>)}
    </div>
  </div>
);

export const Roles = () => (
  <section id="staffing-roles" className="relative py-20 sm:py-28 bg-[#154F49] text-[#FBF8F1] overflow-hidden no-print" data-testid="roles-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12">
      <div className="lg:col-span-5 space-y-8">
        <Reveal className="space-y-4">
          <Eyebrow light>Staffing roles</Eyebrow>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">Credentialed people, <em className="text-[#E2C372]">placed with care.</em></h2>
          <p className="text-[#D6E0D9] leading-relaxed">Six roles across four settings, on schedules that work for facilities, families and our team.</p>
        </Reveal>
        <Reveal delay={0.1} className="relative">
          <div className="rounded-[28px] overflow-hidden aspect-[4/3] [clip-path:polygon(0_0,100%_6%,100%_100%,0_94%)]">
            <img src="/images/team.jpg" srcSet={photoSet("/images/team.jpg", 1200)} sizes="(min-width: 1024px) 50vw, 100vw" decoding="async" alt="Nursing team in a clinical setting" className="w-full h-full object-cover" loading="lazy" />
          </div>
        </Reveal>
        <Reveal delay={0.15} className="space-y-6">
          <Chips id="employment-types" title="Employment" items={EMPLOYMENT_TYPES} />
          <Chips id="shift-types" title="Shifts" items={SHIFTS} />
        </Reveal>
      </div>
      <ul className="lg:col-span-7 border-t border-[#FBF8F1]/15">
        {ROLES.map((r, i) => (
          <Reveal key={r.abbr} delay={i * 0.05}>
            <motion.li initial="rest" whileHover="hover" animate="rest" className="relative border-b border-[#FBF8F1]/15 overflow-hidden" data-testid={`role-${r.abbr.toLowerCase()}`}>
              <motion.span aria-hidden variants={{ rest: { scaleY: 0 }, hover: { scaleY: 1 } }} transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }} className="absolute inset-0 origin-bottom bg-[#CDA34F]" />
              <a href="#apply-online" className="relative flex items-center justify-between gap-4 py-7 sm:py-8 px-2 group">
                <span className="flex items-baseline gap-5 sm:gap-8">
                  <span className="font-serif text-5xl sm:text-7xl leading-none group-hover:text-[#154F49] transition-colors">{r.abbr}</span>
                  <span className="text-sm sm:text-lg text-[#D6E0D9] group-hover:text-[#154F49] transition-colors">{r.name}</span>
                </span>
                <ArrowRight className="w-6 h-6 shrink-0 text-[#E2C372] group-hover:text-[#154F49] group-hover:translate-x-1 transition" />
              </a>
            </motion.li>
          </Reveal>
        ))}
      </ul>
    </div>
  </section>
);
